import type { Appeal, AppealStatus } from "@pax/api-contract/moderation/v1alpha1";
import { APPEAL_COPY } from "@pax/moderation-copy";
import { Button, cn } from "@pax/ui";
import { useMutation } from "@tanstack/react-query";
import { api, reachable, requestErrorMessage } from "@/lib/api";

const PROGRESS_TEXT: Partial<Record<AppealStatus, string>> = {
  awaiting_ai: APPEAL_COPY.submitted,
  awaiting_escalation: APPEAL_COPY.upheldAi,
  awaiting_human: APPEAL_COPY.escalatedHuman,
  upheld: APPEAL_COPY.upheldHuman,
};

const NOTICE = "rounded-lg px-3 py-2 text-sm";
export const QUIET = cn(NOTICE, "bg-foreground/[0.05]");
export const ALERT = cn(NOTICE, "bg-destructive/10 text-destructive");
const LINK = "h-auto px-0 underline";

/** Where an appeal stands, in the copy the user is shown, with what they can still do. */
export function AppealProgress({
  label,
  appeal,
  onChange,
}: {
  label?: string;
  appeal: Appeal;
  onChange: () => unknown;
}) {
  const params = { params: { appealId: appeal.id } };
  const escalate = useMutation({
    mutationFn: () => reachable(api.moderation.appeals.escalate(params)),
    onSuccess: () => onChange(),
  });
  const withdraw = useMutation({
    mutationFn: () => reachable(api.moderation.appeals.withdraw(params)),
    onSuccess: () => onChange(),
  });
  const failure = escalate.error ?? withdraw.error;
  const pending = escalate.isPending || withdraw.isPending;
  const canWithdraw = appeal.status === "awaiting_escalation" || appeal.status === "awaiting_human";
  return (
    <div role="status" className={appeal.status === "upheld" ? ALERT : QUIET}>
      <p>
        {label && <strong className="font-medium">{label}: </strong>}
        {PROGRESS_TEXT[appeal.status]}
      </p>
      {canWithdraw && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3">
          {appeal.status === "awaiting_escalation" && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className={LINK}
              disabled={pending}
              onClick={() => escalate.mutate()}
            >
              {APPEAL_COPY.escalateCta}
            </Button>
          )}
          <Button
            type="button"
            variant="link"
            size="sm"
            className={cn(LINK, "text-muted-foreground")}
            disabled={pending}
            onClick={() => withdraw.mutate()}
          >
            {APPEAL_COPY.withdrawCta}
          </Button>
        </div>
      )}
      {failure && <p className="mt-1 text-destructive">{requestErrorMessage(failure)}</p>}
    </div>
  );
}
